import type { ConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog/types';

export const STANDARD_DRAWER_WIDTHS = {
  XLarge: 'calc(100% - 54px)',
  Larger: 1024,
  Large: 768,
  Medium: 600,
  Small: 520,
} as const;

export const ROOT_MODAL_DRAWER_PROP = 'rootModalDrawer';
export const ROOT_MODAL_PRESENTATION_PROP = 'rootModalPresentation';

export type StandardDrawerWidth = keyof typeof STANDARD_DRAWER_WIDTHS;
export type StandardDrawerVariant = 'temporary' | 'overlay';
export type StandardDrawerCloseTransition = 'shortSlideFade' | 'fullSlideFade';
// The semantic MUI plane a drawer stack occupies. Drawers are normally the base
// surface on the drawer plane, but a drawer launched from a true modal has to
// join the modal plane to paint above the dialog that opened it. RootModal owns
// this decision; feature code should never pick a plane or a z-index.
export type StandardDrawerPlane = 'drawer' | 'modal';

export type DirtyConfirmationOptions = Partial<
  Pick<ConfirmationDialogProps, 'title' | 'content' | 'confirmLabel' | 'denyLabel'>
>;

export type RootModalDrawerOptions = {
  closeTransition?: StandardDrawerCloseTransition;
  dirtyConfirmation?: DirtyConfirmationOptions;
  isDirty?: boolean;
  showScrim?: boolean;
  width?: StandardDrawerWidth;
};

export type RootModalRemoveDialogOptions = {
  animate?: boolean;
};

export const getRootModalRemoveDialogOptions = (
  rootModalDrawer?: RootModalDrawerOptions,
): RootModalRemoveDialogOptions | undefined => (rootModalDrawer ? { animate: true } : undefined);

/**
 * A sub-flow launched from a drawer (e.g. the details drawer) often needs a different width than the
 * base drawer it was launched from. Returns a copy of the options with the width overridden, or
 * `undefined` when there is no drawer — so the non-drawer / floating-modal flow stays unaffected.
 */
export const withDrawerWidth = (
  options: RootModalDrawerOptions | undefined,
  width: StandardDrawerWidth,
): RootModalDrawerOptions | undefined => (options ? { ...options, width } : undefined);
